/**
 * ACT2-1108: the "Set up Lookout" panel on the chat home, for owners and admins.
 *
 * The steps come from `SETUP_STEPS`; one is open as a card and the rest are one line each. The panel opens on the first step still
 * to do. After that only the person moves it: a step stays open after it gets done, so they can connect,
 * link, note or invite one more, until they press Continue or close it. Progress is workspace-wide
 * (`setup.status`, re-read every 15s while the panel shows), so a step any admin did or skipped is done for
 * everyone. Once every step is done or skipped, the server records completion once and the panel never comes
 * back. "Hide" is per person; the sidebar's Setup entry brings the panel back while setup is incomplete.
 */
import { useQuery } from '@tanstack/react-query';
import { lazy, Suspense, useState } from 'react';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { Button } from '@/_shared/components/ui/button';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import type { SetupProgress } from '@/_shared/hooks/useSetupProgress';
import {
  SETUP_STEP_IDS,
  SETUP_STEP_TOTAL,
  SETUP_STEPS,
  type SetupStepConfig,
  type SetupStepId,
} from '@/_shared/lib/setup-steps';
import { nameWarehouseAccounts } from '@/settings/components/warehouseAccount/useWarehouseAccounts';

import { usePendingInvites } from '../hooks/usePendingInvites';
import { BackgroundJobs } from './BackgroundJobs';
import { InviteStep } from './InviteStep';
import { SettingsStep } from './SettingsStep';
import { SetupStep } from './SetupStep';
import { WarehouseStep } from './WarehouseStep';

// The repo and note steps load when the panel first draws them, not with the home page: the chat's in-chat
// assist card shares them, and the first page's JavaScript has a budget (ACT2-1008).
const ReposStep = lazy(() => import('./ReposStep.js').then((m) => ({ default: m.ReposStep })));
const ContextStep = lazy(() => import('./ContextStep.js').then((m) => ({ default: m.ContextStep })));

/** What "Try it" puts in the composer: editable, not sent. */
export const TRY_AUDIENCE_PROMPT = 'Build me an audience of high-value customers';

interface SetupPanelProps {
  /** The panel itself, focusable, so the home can move focus to it when it brings the panel back. */
  ref?: React.Ref<HTMLElement>;
  progress: SetupProgress;
  /**
   * Puts `TRY_AUDIENCE_PROMPT` in the home composer and focuses it. Absent while the composer is not
   * shown (no warehouse yet), which disables "Try it".
   */
  onTryAudience?: () => void;
}

/** Names in one subtitle line: every name, comma-separated; the step's line truncates what does not fit. */
function namesLine(names: readonly string[]): string | undefined {
  return names.length > 0 ? names.join(', ') : undefined;
}

/** The first step still to do after `from` in panel order, else the first still to do anywhere, else null. */
function nextOpenStep(from: SetupStepId, done: Record<SetupStepId, boolean>): SetupStepId | null {
  const after = SETUP_STEP_IDS.slice(SETUP_STEP_IDS.indexOf(from) + 1);
  return after.find((id) => !done[id]) ?? SETUP_STEP_IDS.find((id) => id !== from && !done[id]) ?? null;
}

export function SetupPanel({ ref, progress, onTryAudience }: SetupPanelProps): React.ReactElement | null {
  const { isAudiences } = useWorkspace();
  const orpc = useOrpc();
  // The settings step shows the Slack switch only while Slack is connected; its line names what it shows.
  const { data: slack } = useQuery(orpc.slack.getInstallStatus.queryOptions({ input: {} }));
  const { status, done, doneCount, currentStep, complete, prefs } = progress;
  // What a done step did, shown as its subtitle: the connected warehouses, the linked repos, the invites.
  const { data: accountsData } = useQuery({
    ...orpc.settings.warehouses.listAccounts.queryOptions(),
    enabled: done?.warehouse === true,
  });
  const { data: projectsData } = useQuery({
    ...orpc.warehouseProjects.list.queryOptions({ input: {} }),
    enabled: status?.reposLinked === true,
  });
  const pendingInvites = usePendingInvites();

  // Which step is open. `undefined` until the status first loads; then the first step still to do, and from
  // there only the person changes it. A status re-read never closes or moves the open step, so a step that
  // gets done while open stays open on what was done and the way to do one more.
  const [open, setOpen] = useState<SetupStepId | null | undefined>(undefined);
  if (open === undefined && done !== null) setOpen(currentStep);
  const expanded = open === undefined ? currentStep : open;
  const toggle = (step: SetupStepId): void => setOpen(expanded === step ? null : step);
  const advance = (from: SetupStepId): void => setOpen(done ? nextOpenStep(from, done) : null);

  if (!status || !done) return null;

  const warehouseNote = namesLine(nameWarehouseAccounts(accountsData?.accounts ?? []).map((a) => a.name));
  const reposNote = status.reposLinked
    ? // One row per account key (ACT2-1133): a repo linked to two keys is listed once.
      namesLine([...new Set((projectsData?.projects ?? []).map((project) => project.repoFullName))])
    : status.reposSkipped
      ? 'Skipped'
      : undefined;
  const invitesNote = namesLine(pendingInvites.map((invite) => invite.email));
  const contextNote = !status.contextAdded && status.contextSkipped ? 'Skipped' : undefined;
  const inviteNote = invitesNote
    ? `Invite sent · ${invitesNote}`
    : !status.inviteSent && status.inviteSkipped
      ? 'Skipped'
      : undefined;

  // What a done step did, shown instead of its description.
  const doneNotes: Partial<Record<SetupStepId, string>> = {
    warehouse: warehouseNote && `Connected · ${warehouseNote}`,
    repos: reposNote,
    context: contextNote,
    invite: inviteNote,
  };
  const descriptions: Partial<Record<SetupStepId, string>> = {
    // The settings step shows the Slack switch only while Slack is connected.
    settings: slack?.installed === true ? 'AI controls, Slack guest access, public dashboards.' : undefined,
  };
  const bodies: Record<SetupStepId, React.ReactNode> = {
    warehouse: <WarehouseStep />,
    repos: (
      <Suspense fallback={<StepLoading label="Loading the repo step" />}>
        <ReposStep status={status} />
      </Suspense>
    ),
    context: (
      <Suspense fallback={<StepLoading label="Loading the note step" />}>
        <ContextStep status={status} />
      </Suspense>
    ),
    settings: (
      <SettingsStep
        onDone={() => {
          prefs.setSettingsReviewed(true);
          advance('settings');
        }}
      />
    ),
    invite: <InviteStep status={status} />,
  };

  const stepProps = ({ id, ...step }: SetupStepConfig) => ({
    done: done[id],
    current: currentStep === id,
    expanded: expanded === id,
    onToggle: () => toggle(id),
    // A done (or skipped) step's way forward. Absent while it is still to do: its own controls finish it.
    // A step whose own control moves on (`ownContinue`) gets none.
    onContinue: done[id] && step.ownContinue !== true ? () => advance(id) : undefined,
    continueLabel: nextOpenStep(id, done) === null ? 'Done' : 'Continue',
  });

  return (
    // Docked beside the chat, so every rem it takes comes out of the chat column. Below `lg` it stacks
    // under the chat at full width. From `lg` it is 26rem; from 100rem (1600px) wide it is the side
    // drawer's width (`w-drawer`, the same token as the account drawers). With the 15rem sidebar open,
    // that keeps the chat column at 49rem or more from 1440px up, room for the 680px composer.
    <aside
      ref={ref}
      tabIndex={-1}
      aria-labelledby="setup-panel-title"
      className="flex w-full shrink-0 flex-col gap-4 border-t border-border bg-background p-4 outline-none lg:w-[26rem] lg:overflow-y-auto lg:border-t-0 lg:border-l min-[100rem]:w-drawer"
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <h2 id="setup-panel-title" className="text-base font-semibold text-foreground">
            Set up Lookout
          </h2>
          <Button type="button" variant="tertiary" size="sm" label="Hide" onClick={() => prefs.setHidden(true)} />
        </div>
        {complete && <p className="text-sm text-muted-foreground">You’re set up. You can hide this panel.</p>}
      </div>

      <div className="flex items-center gap-3">
        <div
          role="progressbar"
          aria-label="Setup progress"
          aria-valuemin={0}
          aria-valuemax={SETUP_STEP_TOTAL}
          aria-valuenow={doneCount}
          className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted"
        >
          <div
            className="h-full rounded-full bg-primary transition-[width]"
            style={{ width: `${Math.round((doneCount / SETUP_STEP_TOTAL) * 100)}%` }}
          />
        </div>
        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
          {doneCount} of {SETUP_STEP_TOTAL}
        </span>
      </div>

      <ol className="flex flex-col gap-1">
        {SETUP_STEPS.map((step, index) => (
          <SetupStep
            key={step.id}
            n={index + 1}
            title={step.title}
            tag={step.tag}
            description={descriptions[step.id] ?? step.description}
            doneNote={doneNotes[step.id]}
            {...stepProps(step)}
          >
            {bodies[step.id]}
          </SetupStep>
        ))}
      </ol>

      {isAudiences && (
        <div className="flex items-center gap-3 border-t border-border px-3 pt-4">
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="text-sm font-medium text-foreground">Then: build your first audience</span>
            <span className="text-xs text-muted-foreground">Describe it in chat, then activate it.</span>
          </span>
          <ActionTooltip show={!status.warehouseConnected || !onTryAudience} message="Connect a warehouse first.">
            <Button
              type="button"
              variant={complete ? 'primary' : 'secondary'}
              size="sm"
              label="Try it"
              disabled={!status.warehouseConnected || !onTryAudience}
              onClick={onTryAudience}
            />
          </ActionTooltip>
        </div>
      )}

      {/* Last, so a job row coming or going never moves a step's controls under the pointer. */}
      <BackgroundJobs />
    </aside>
  );
}

/** A step body while its code loads. */
function StepLoading({ label }: { label: string }): React.ReactElement {
  return (
    <div role="status" aria-label={label}>
      <Skeleton className="h-12 w-full" />
    </div>
  );
}
